import type { ErrorCode, FieldError } from '@aurora/contracts';

/**
 * The HTTP client.
 *
 * Requests go to `/api/v1` on the same origin — Vite proxies it in development
 * and a reverse proxy serves it in production — so the session cookie behaves
 * identically in both, and there is no CORS to configure or get wrong.
 */

const BASE = '/api/v1';

/**
 * The API prefix, for the two things that cannot go through `api()`: a
 * multipart upload, which needs the browser to set its own boundary, and an
 * attachment link, which is a plain `href` the browser fetches itself.
 */
export const API_BASE = BASE;

/** Mirrors the server's error envelope, so a caller can branch on `code`. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'network_error',
    message: string,
    readonly details?: FieldError[],
    readonly requestId?: string,
    /** Seconds to wait before trying again, on a 429. */
    readonly retryAfter?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field errors keyed by path, ready to hand to a form. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details ?? []) out[detail.path] = detail.message;
    return out;
  }

  get isAuthError(): boolean {
    return this.status === 401;
  }

  /**
   * The session this request relied on is gone — expired, revoked, signed out
   * elsewhere — as opposed to a 401 that is an answer in its own right.
   *
   * A wrong password and a wrong two-factor code are also 401s
   * (`invalid_credentials`), and treating those as "you have been signed out"
   * would bounce somebody off the sign-in page for a typo.
   */
  get endsSession(): boolean {
    return this.status === 401 && (this.code === 'unauthenticated' || this.code === 'session_expired');
  }

  /**
   * The server or the network failed, rather than refusing the request.
   *
   * The only errors worth a request id on screen: a reference turns "it broke"
   * into a log line support can find. Printing one under "Incorrect password"
   * just makes an ordinary mistake look like a crash.
   */
  get isServerFault(): boolean {
    return this.status >= 500 || this.code === 'network_error';
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, headers, ...rest } = options;

  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...rest,
      // The session lives in an HttpOnly cookie, so it must be sent explicitly.
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    // A failed fetch means offline, DNS, or a dropped connection — never a
    // server-side rejection. Saying so is more useful than "failed to fetch".
    throw new ApiError(
      0,
      'network_error',
      'Could not reach the server. Check your connection and try again.',
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? safeParse(text) : undefined;

  if (!response.ok) {
    const envelope = payload as
      | {
          error?: {
            code: ErrorCode;
            message: string;
            details?: FieldError[];
            request_id?: string;
            retry_after?: number;
          };
        }
      | undefined;
    const error = envelope?.error;
    const retryAfter = error?.retry_after ?? Number(response.headers.get('retry-after') ?? Number.NaN);
    throw new ApiError(
      response.status,
      error?.code ?? 'internal_error',
      error?.message ?? `Request failed with status ${response.status}`,
      error?.details,
      error?.request_id ?? response.headers.get('x-request-id') ?? undefined,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    );
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export const get = <T>(path: string, query?: RequestOptions['query']) =>
  api<T>(path, { method: 'GET', ...(query ? { query } : {}) });

export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body });

export const patch = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body });

/**
 * `PUT` replaces a collection wholesale.
 *
 * One endpoint uses it — group membership, which is sent as the complete list
 * rather than as a series of adds and removes. Without this helper that route
 * was simply unreachable: the endpoint existed, a seeded role granted the
 * permission for it, and no screen could call it, so nobody could ever be put
 * into a group.
 */
export const put = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body });

/**
 * `DELETE` carries a body in one place: turning off two-factor authentication,
 * which requires a current code rather than only a session. HTTP permits it and
 * the alternative — a code in the query string — would put a live credential in
 * the request log.
 */
export const del = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'DELETE', ...(body === undefined ? {} : { body }) });
